import React, { useState } from "react";
import { Button, Tooltip } from "@mui/material";
import { CircleOutlined, Close, SwapHoriz } from "@mui/icons-material";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import ftsmartkeyIcon from "../../../assets/images/ftsmartkey_icon_small.png";
import mirairodoorIcon from "../../../assets/images/mirairodoor_icon_small.png";
import IconButtonWithTooltip from "../../atoms/Button/IconButtonWithTooltip";
import { NavigateFunction } from "react-router-dom";
import {
  NewSdcReplacementModal,
  Sdc as ModalPropsSdc,
} from "./NewSdcReplacementModal";
import ConfirmDialog from "../../molecules/ConfirmDialog";
import { getJwtTokenFromIdToken } from "../auth/AuthFanc";
import { GridColDef, GridRenderCellParams } from "@mui/x-data-grid";
import {
  customDataGridLocaleTextNoData,
  StyledDataGrid,
} from "../../../styles/styles";
import SimOperationModal from "./SimOperationModal";

export type Sdc = {
  createdAt: string | number | Date;
  autoSdcId: number;
  sdcUuid: string;
  sdcSerialNumber: string;
  isEnabled: boolean;
  doorPlace: string;
  memo: string;
  isOnline: boolean;
  isAalEnabled: boolean;
  isSciEnabled: boolean;
  simId: string;
  simStatus: string;
  simOperation: string;
  billingStartAt: string | null;
  lastDisconnectedAt: string | number | Date;
  lastDownloadedAt: string | number | Date;
};

export type BuildingSdcsTableProps = {
  sdcs: Sdc[];
  navigate: NavigateFunction;
  onSuccess: (message: string) => void;
  onError: (message: string) => void;
};

export const BuildingSdcsTable: React.FC<BuildingSdcsTableProps> = (props) => {
  const [doorDeleteConfirmOpen, setDoorDeleteConfirmOpen] =
    React.useState(false);
  const [selectedSdcIdOnDeletion, setSelectedSdcIdOnDeletion] =
    React.useState<number>();
  const [doorDeletedConfirmOpen, setDoorDeletedConfirmOpen] =
    React.useState(false);

  const [openSdcReplacementModalWith, setOpenSdcReplacementModalWith] =
    React.useState<ModalPropsSdc | null>(null);
  const [simOperationModalOpen, setSimOperationModalOpen] = useState(false);
  const [selectedSdc, setSelectedSdc] = useState<Sdc | null>(null);
  const [selectedOperation, setSelectedOperation] = useState<
    "testing" | "waiting_running" | null
  >(null);

  const handleDeleteDoor = (autoSdcId: number) => {
    setSelectedSdcIdOnDeletion(autoSdcId);
    setDoorDeleteConfirmOpen(true);
  };

  const handleSimOperation = (
    sdc: Sdc,
    operation?: "testing" | "waiting_running"
  ) => {
    setSelectedSdc(sdc);
    setSelectedOperation(operation || null);
    setSimOperationModalOpen(true);
  };

  const handleSimOperationSuccess = (message: string) => {
    props.onSuccess(message);
    // 3秒後にリロード
    setTimeout(() => {
      window.location.reload();
    }, 3000);
  };

  const renderSimOperationButton = (params: GridRenderCellParams<Sdc>) => {
    const { simOperation, simId } = params.row;

    if (simId === "") {
      return <span>SIM未設定</span>;
    }

    switch (simOperation) {
      case "waiting_operation":
        return (
          <>
            <Button
              variant="contained"
              size="small"
              style={{
                marginRight: "8px",
                backgroundColor: "#f44336", // 赤色 (試験)
                color: "white",
              }}
              onClick={() => handleSimOperation(params.row, "testing")}
            >
              試験
            </Button>
            <Button
              variant="contained"
              size="small"
              style={{
                backgroundColor: "#4caf50", // 緑色 (運用)
                color: "white",
              }}
              onClick={() => handleSimOperation(params.row, "waiting_running")}
            >
              運用
            </Button>
          </>
        );
      case "waiting_running":
        return (
          <Button
            variant="contained"
            size="small"
            style={{
              backgroundColor: "#2196f3", // 青色 (運用待ち)
              color: "white",
            }}
            onClick={() => handleSimOperation(params.row)}
          >
            運用待ち
          </Button>
        );
      case "in_running":
        return (
          <Button
            variant="contained"
            size="small"
            style={{
              backgroundColor: "#ff9800", // オレンジ色 (運用中)
              color: "black",
            }}
            onClick={() => handleSimOperation(params.row)}
          >
            運用中
          </Button>
        );
      case "testing":
        return (
          <Button
            variant="contained"
            size="small"
            style={{
              backgroundColor: "#ffc107", // 黄色 (試験中)
              color: "black",
            }}
            onClick={() => handleSimOperation(params.row)}
          >
            試験中
          </Button>
        );
      default:
        return <span>操作不可</span>;
    }
  };

  const columns: GridColDef[] = [
    {
      field: "sdcSerialNumber",
      headerName: "SDCシリアルナンバー",
      flex: 2,
      renderCell: (params: GridRenderCellParams<Sdc>) => (
        <Tooltip
          title={
            `${params.row.sdcSerialNumber} ` + `(UUID: ${params.row.sdcUuid})`
          }
        >
          <span>{params.row.sdcSerialNumber}</span>
        </Tooltip>
      ),
    },
    {
      field: "onlineStatus",
      headerName: "接続状態",
      flex: 1,
      renderCell: (params: GridRenderCellParams<Sdc>) => (
        <Tooltip title={params.row.isOnline ? "オンライン" : "オフライン"}>
          {params.row.isOnline ? (
            <CircleOutlined sx={{ color: "green" }} />
          ) : (
            <Close sx={{ color: "red" }} />
          )}
        </Tooltip>
      ),
    },
    {
      field: "lastDisconnectedAt",
      headerName: "最終切断日時",
      flex: 1.5,
      renderCell: (params) => {
        if (!params.value) {
          // nullまたはundefinedの場合の表示
          return (
            <Tooltip title="記録なし">
              <span>記録なし</span>
            </Tooltip>
          );
        }
    
        const dateTimeStr = new Date(params.value).toLocaleString("ja-JP", {
          timeZone: "Asia/Tokyo", // 日本時間
          year: "numeric",
          month: "2-digit",
          day: "2-digit",
          hour: "2-digit",
          minute: "2-digit",
          second: "2-digit",
        });
    
        return (
          <Tooltip title={dateTimeStr}>
            <span>{dateTimeStr}</span>
          </Tooltip>
        );
      },
    },    
    {
      field: "enabledServices",
      headerName: "有効サービス",
      flex: 1,
      renderCell: (params: GridRenderCellParams<Sdc>) => (
        <React.Fragment>
          {params.row.isAalEnabled && (
            <Tooltip title="FTスマートキー">
              <img
                src={ftsmartkeyIcon}
                alt="FTスマートキー icon"
                style={{
                  height: "30px",
                  marginRight: "3px",
                }}
              />
            </Tooltip>
          )}
          {params.row.isSciEnabled && (
            <Tooltip title="ミライロドア">
              <img
                src={mirairodoorIcon}
                alt="ミライロドア icon"
                style={{
                  height: "30px",
                }}
              />
            </Tooltip>
          )}
        </React.Fragment>
      ),
    },
    {
      field: "lastDownloadedAt",
      headerName: "SDCが最後に鍵をDLした日時",
      flex: 1.5,
      renderCell: (params: GridRenderCellParams<Sdc>) => {
        if (!params.value) {
          return (
            <Tooltip title="記録なし">
              <span>記録なし</span>
            </Tooltip>
          );
        }

        const dateStr = new Date(params.value).toLocaleString("ja-JP", {
          timeZone: "Asia/Tokyo",
          year: "numeric",
          month: "2-digit",
          day: "2-digit",
          hour: "2-digit",
          minute: "2-digit",
          second: "2-digit",
        });
        return (
          <Tooltip title={dateStr}>
            <span>{dateStr}</span>
          </Tooltip>
        );
      },
    },
    {
      field: "doorPlace",
      headerName: "設置場所",
      flex: 3,
      renderCell: (params: GridRenderCellParams<Sdc>) => (
        <Tooltip title={params.row.doorPlace}>
          <span>{params.row.doorPlace}</span>
        </Tooltip>
      ),
    },
    {
      field: "memo",
      headerName: "メモ",
      flex: 3,
      renderCell: (params: GridRenderCellParams<Sdc>) => (
        <Tooltip title={params.row.memo}>
          <span>{params.row.memo}</span>
        </Tooltip>
      ),
    },
    {
      field: "actions",
      headerName: "操作",
      flex: 2,
      renderCell: (params: GridRenderCellParams<Sdc>) => (
        <React.Fragment>
          <IconButtonWithTooltip
            title={"編集"}
            onClick={() => props.navigate("/doors/" + params.row.autoSdcId)}
            icon={<EditIcon />}
          ></IconButtonWithTooltip>
          <IconButtonWithTooltip
            title={"削除"}
            onClick={() => handleDeleteDoor(params.row.autoSdcId)}
            icon={<DeleteIcon />}
          ></IconButtonWithTooltip>
          <IconButtonWithTooltip
            title={"交換"}
            onClick={() =>
              setOpenSdcReplacementModalWith({
                doorPlace: params.row.doorPlace,
                sdcSerialNumber: params.row.sdcSerialNumber,
                sdcUuid: params.row.sdcUuid,
                memo: params.row.memo,
                shippingDate: null // 交換元SDCの出荷日は不要なのでnullを設定
              })
            }
            icon={<SwapHoriz />}
          ></IconButtonWithTooltip>
        </React.Fragment>
      ),
    },
    {
      field: "simOperation",
      headerName: "SIM操作",
      flex: 2,
      renderCell: renderSimOperationButton,
    },
    {
      field: "createdAt",
      headerName: "登録日",
      flex: 1.5,
      renderCell: (params: GridRenderCellParams<Sdc>) => {
        const date = new Date(params.row.createdAt).toLocaleString("ja-JP", {
          year: "numeric",
          month: "2-digit",
          day: "2-digit",
        });
        return (
          <Tooltip title={date}>
            <span>{date}</span>
          </Tooltip>
        );
      },
    },
    {
      field: "billingStartAt",
      headerName: "課金開始日",
      flex: 1.5,
      renderCell: (params: GridRenderCellParams<Sdc>) => {
        const date = params.row.billingStartAt
          ? new Date(params.row.billingStartAt).toLocaleString("ja-JP", {
              year: "numeric",
              month: "2-digit",
              day: "2-digit",
            })
          : "-";
        return (
          <Tooltip title={date}>
            <span>{date}</span>
          </Tooltip>
        );
      },
    },
  ];

  return (
    <React.Fragment>
      <div style={{ height: 300, width: "100%" }}>
        <StyledDataGrid
          rows={props.sdcs}
          columns={columns}
          getRowId={(row) => row.autoSdcId}
          disableRowSelectionOnClick
          localeText={
            props.sdcs.length === 0 ? customDataGridLocaleTextNoData : {}
          }
        />
      </div>

      <ConfirmDialog
        open={doorDeleteConfirmOpen}
        onClose={() => {
          setDoorDeleteConfirmOpen(false);
        }}
        onConfirm={async () => {
          const jwtToken = await getJwtTokenFromIdToken();
          try {
            // call detachSdc API
            const response = await fetch(
              process.env.REACT_APP_API_ENDPOINT +
                `/sdcs/${selectedSdcIdOnDeletion}/detach-from-building`,
              {
                method: "PATCH",
                headers: {
                  "Content-Type": "application/json",
                  Authorization: jwtToken,
                },
                body: JSON.stringify({
                  autoSdcId: selectedSdcIdOnDeletion,
                }),
              }
            );

            if (response.ok) {
              setDoorDeletedConfirmOpen(true);
            } else {
              props.onError("削除に失敗しました。");
            }
          } catch (error) {
            props.onError("エラーが発生しました。");
            console.error(error);
          }
        }}
        title="確認"
        description="このSDC（ドア）を削除してもよろしいですか？"
      />

      <ConfirmDialog
        open={doorDeletedConfirmOpen}
        onClose={() => {
          setDoorDeletedConfirmOpen(false);
        }}
        onConfirm={() => {
          window.location.reload();
        }}
        title="確認"
        description="削除が完了しました。"
        showCancelButton={false}
      />

      <NewSdcReplacementModal
        open={openSdcReplacementModalWith !== null}
        close={() => {
          setOpenSdcReplacementModalWith(null);
        }}
        sourceSdc={
          openSdcReplacementModalWith ?? {
            doorPlace: "",
            sdcSerialNumber: "",
            sdcUuid: "",
            memo: "",
            shippingDate: null
          }
        }
        onSuccess={props.onSuccess}
        onError={props.onError}
      />

      {selectedSdc && (
        <SimOperationModal
          open={simOperationModalOpen}
          onClose={() => {
            setSimOperationModalOpen(false);
            setSelectedOperation(null);
          }}
          autoSdcId={selectedSdc.autoSdcId}
          simId={selectedSdc.simId}
          currentOperation={selectedSdc.simOperation}
          selectedOperation={selectedOperation}
          onSuccess={handleSimOperationSuccess}
          onError={props.onError}
        />
      )}
    </React.Fragment>
  );
};
